import React, { useState, useRef, useCallback, useLayoutEffect, useEffect } from "react";
import { createPortal } from "react-dom";
import { motion } from "framer-motion";
import { Fragment } from "react";
import {
  FiChevronUp,
  FiChevronDown,
  FiChevronLeft,
  FiChevronRight,
} from "react-icons/fi";
import { Eye, CheckCircle, XCircle, Loader2, X, Inbox, MoreVertical, Archive, FolderTree } from "lucide-react";
import DeclineDealModal from "./DeclineDealModal";
import ViewDealModal from "./ViewDealModal";
import { temporarilyDeleteDeal } from "../../requests/Uploaded_Deals/UploadedDealsService";
import { formatDate } from "../../utils/Utils";
import { truncateSentence } from "../../utils/truncateSentence";
import AcceptDealModal from "./AcceptDealModal";
import AddToArchiveModal from "./AddToArchiveModal";
import ChangeDealCategoryDrawer from "./ChangeDealCategoryDrawer";
import moment from "moment";

const MENU_ITEM_CLASS =
  "flex items-center gap-2.5 w-full px-3 py-2.5 text-left text-sm font-medium rounded-lg transition-colors";

const ActionMenu = ({ row, onSuccess, onError, isFirstRow, refreshData, totalRows }) => {
  const [open, setOpen] = useState(false);
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0, bottom: undefined, isDropup: false });
  const triggerRef = useRef(null);

  const [showDeclinetModal, setShowDeclinetModal] = useState(false);
  const [showAcceptModal, setShowAcceptModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showArchiveModal, setShowArchiveModal] = useState(false);
  const [showCategoryDrawer, setShowCategoryDrawer] = useState(false);
  const [archiveLoading, setArchiveLoading] = useState(false);
  const [deal, setDeal] = useState(null);

  const updatePosition = useCallback(() => {
    if (!triggerRef.current || !open) return;
    const rect = triggerRef.current.getBoundingClientRect();
    const padding = 8;
    // Use bottom positioning for dropup so the menu sits just above the button (no fixed height guess)
    const estimatedMenuHeight = 248;
    const spaceBelow = window.innerHeight - rect.bottom;
    const isDropup = spaceBelow < estimatedMenuHeight && rect.top > padding;
    const left = Math.min(rect.left, window.innerWidth - 200);
    const top = !isDropup ? rect.bottom + padding : undefined;
    const bottom = isDropup ? window.innerHeight - rect.top + padding : undefined;
    setMenuPosition({ top, bottom, left, isDropup });
  }, [open]);

  useLayoutEffect(() => {
    if (open) updatePosition();
  }, [open, updatePosition]);

  useEffect(() => {
    if (!open) return;
    const onScroll = () => updatePosition();
    window.addEventListener("scroll", onScroll, true);
    window.addEventListener("resize", updatePosition);
    return () => {
      window.removeEventListener("scroll", onScroll, true);
      window.removeEventListener("resize", updatePosition);
    };
  }, [open, updatePosition]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (open && triggerRef.current && !triggerRef.current.contains(e.target) &&
          !e.target.closest(".deals-action-menu-portal")) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [open]);

  const handleDecline = () => {
    setOpen(false);
    setShowDeclinetModal(true);
  };

  const handleAccept = () => {
    setDeal(row);
    setOpen(false);
    setShowAcceptModal(true);
  };

  const handleFeedbackSubmit = () => {
    setShowDeclinetModal(false);
  };

  const handleView = () => {
    setOpen(false);
    setShowViewModal(true);
  };

  const handleAddToArchive = () => {
    setOpen(false);
    setShowArchiveModal(true);
  };

  const handleChangeCategory = () => {
    setOpen(false);
    setShowCategoryDrawer(true);
  };

  const handleArchiveConfirm = async () => {
    const slug = row?.deal_slug;
    if (!slug) return;
    setArchiveLoading(true);
    try {
      await temporarilyDeleteDeal(slug, {
        onSuccess: () => {
          setArchiveLoading(false);
          setShowArchiveModal(false);
          if (typeof onSuccess === "function") onSuccess();
        },
        onError: () => {
          setArchiveLoading(false);
        },
      });
    } catch {
      setArchiveLoading(false);
    }
  };

  return (
    <>
      <div className="flex relative justify-center items-center">
        <button
          ref={triggerRef}
          type="button"
          onClick={() => setOpen((o) => !o)}
          className={`p-2 rounded-lg border transition-all ${
            open
              ? "bg-[#FF6C6C]/10 border-[#FF6C6C]/30 text-[#FF6C6C]"
              : "border-gray-200 text-gray-500 hover:bg-gray-100 hover:border-gray-300"
          }`}
          aria-haspopup="true"
          aria-expanded={open}
          title="Actions"
        >
          <MoreVertical className="w-4 h-4" />
        </button>
      </div>

      {open &&
        createPortal(
          <div
            className="deals-action-menu-portal fixed z-[9999] min-w-[180px] py-1.5 bg-white rounded-xl border border-gray-200 shadow-xl"
            style={{
              left: menuPosition.left,
              ...(menuPosition.isDropup ? { bottom: menuPosition.bottom } : { top: menuPosition.top }),
            }}
          >
            <button
              type="button"
              onClick={handleView}
              className={`text-gray-700 ${MENU_ITEM_CLASS} hover:bg-blue-50 hover:text-blue-700`}
            >
              <Eye className="w-4 h-4 text-blue-500 shrink-0" />
              View details
            </button>
            <button
              type="button"
              onClick={handleChangeCategory}
              className={`text-gray-700 ${MENU_ITEM_CLASS} hover:bg-[#FFF1F0] hover:text-[#CC2E2E]`}
            >
              <FolderTree className="w-4 h-4 text-[#FF6C6C] shrink-0" />
              Change category
            </button>
            {row.status !== "Approved" && (
              <button
                type="button"
                onClick={handleAccept}
                className={`text-gray-700 ${MENU_ITEM_CLASS} hover:bg-emerald-50 hover:text-emerald-700`}
              >
                <CheckCircle className="w-4 h-4 text-emerald-500 shrink-0" />
                Accept deal
              </button>
            )}
            <button
              type="button"
              onClick={handleDecline}
              className={`text-gray-700 ${MENU_ITEM_CLASS} hover:bg-red-50 hover:text-red-700`}
            >
              <XCircle className="w-4 h-4 text-red-500 shrink-0" />
              Decline
            </button>
            <button
              type="button"
              onClick={handleAddToArchive}
              className={`pt-2 mt-1 text-gray-700 border-t border-gray-100 ${MENU_ITEM_CLASS} hover:bg-amber-50 hover:text-amber-700`}
            >
              <Archive className="w-4 h-4 text-amber-500 shrink-0" />
              Add to Archive
            </button>
          </div>,
          document.body
        )}

      <ViewDealModal
        viewModal={showViewModal}
        onCloseViewModal={() => setShowViewModal(false)}
        deal={row}
      />

      <DeclineDealModal
        isOpen={showDeclinetModal}
        onClose={() => setShowDeclinetModal(false)}
        onSubmit={handleFeedbackSubmit}
        deal={row}
        dealId={row.deal_slug}
        onSuccess={onSuccess}
        onError={onError}
      />

      <AcceptDealModal
        isOpen={showAcceptModal}
        onClose={() => setShowAcceptModal(false)}
        deal={deal || row}
        dealId={row.deal_slug}
        onSuccess={onSuccess}
        onError={onError}
      />

      <AddToArchiveModal
        isOpen={showArchiveModal}
        onClose={() => setShowArchiveModal(false)}
        onConfirm={handleArchiveConfirm}
        deal={row}
        isLoading={archiveLoading}
      />

      <ChangeDealCategoryDrawer
        isOpen={showCategoryDrawer}
        onClose={() => setShowCategoryDrawer(false)}
        onSaved={onSuccess}
        deal={row}
      />
    </>
  );
};

const DealImageThumbnail = ({ deal, getDealImageUrl }) => {
  const [imgError, setImgError] = useState(false);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const url = getDealImageUrl(deal);
  const showPlaceholder = !url || imgError;
  const canOpenLightbox = url && !imgError;

  return (
    <>
      <button
        type="button"
        onClick={() => canOpenLightbox && setLightboxOpen(true)}
        className={`flex items-center justify-center w-12 h-12 rounded-lg overflow-hidden bg-gray-100 border border-gray-200/80 flex-shrink-0 ${canOpenLightbox ? "cursor-pointer hover:ring-2 hover:ring-[#FF6C6C]/50 hover:border-[#FF6C6C]/50 transition-all" : "cursor-default"}`}
      >
        {!showPlaceholder && (
          <img
            src={url}
            alt=""
            className="object-cover w-full h-full pointer-events-none"
            loading="lazy"
            onError={() => setImgError(true)}
          />
        )}
        {showPlaceholder && (
          <span className="flex justify-center items-center w-full h-full text-xs font-medium text-gray-400">
            —
          </span>
        )}
      </button>

      {lightboxOpen && url && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
          onClick={() => setLightboxOpen(false)}
          role="dialog"
          aria-modal="true"
          aria-label="View deal image"
        >
          <button
            type="button"
            onClick={() => setLightboxOpen(false)}
            className="absolute top-4 right-4 p-2 text-white rounded-full transition-colors bg-white/10 hover:bg-white/20"
            aria-label="Close"
          >
            <X className="w-6 h-6" />
          </button>
          <div
            className="relative max-w-[90vw] max-h-[90vh] flex items-center justify-center"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={url}
              alt={deal?.title ? `Deal: ${deal.title}` : "Deal image"}
              className="max-w-full max-h-[90vh] w-auto h-auto object-contain rounded-lg shadow-2xl"
            />
          </div>
        </div>
      )}
    </>
  );
};

const TableComponent = ({
  data,
  searchTerm = "",
  sortConfig,
  onSort,
  onSuccess,
  refreshData,
  onSelectedRowsChange,
  statusFilter = "all",
  serverPagination = null,
  onPageChange,
  onItemsPerPageChange,
  isLoading = false,
  onClearFilters,
}) => {
  const [clientPage, setClientPage] = useState(1);
  const [clientItemsPerPage, setClientItemsPerPage] = useState(5);

  const isServerSide = serverPagination != null && typeof onPageChange === "function";

  // Reset to first page when filter changes (client-side only)
  React.useEffect(() => {
    if (!isServerSide) setClientPage(1);
  }, [statusFilter, isServerSide]);

  // Helper function to format currency
  const formatCurrency = (amount) => {
    if (amount === null || amount === undefined) return "N/A";
    return new Intl.NumberFormat("en-GH", {
      style: "currency",
      currency: "GHS",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })
      .format(amount)
      .replace("GHS", "GH₵");
  };

  // Helper function to check if a deal is expired
  const isExpired = (expiryDate) => {
    if (!expiryDate) return false;
    const currentDate = new Date();
    const expiry = new Date(expiryDate);
    return expiry < currentDate;
  };

  // Helper function to check if a deal is active (Approved and not expired)
  const isActiveDeal = (deal) => {
    // Check flash sale deals first
    if (deal?.validity_type === "flash_sale") {
      if (deal?.flash && Array.isArray(deal.flash) && deal.flash.length > 0) {
        const isFlashExpired = deal.flash.some(flash => moment(flash.flash_end_time).isBefore(moment()));
        const isFlashActive = deal.flash.some(flash => 
          moment().isBetween(moment(flash.flash_start_time), moment(flash.flash_end_time))
        );
        return deal.status === "Approved" && isFlashActive && !isFlashExpired;
      }
      return deal.status === "Approved";
    }
    // Regular deals
    return deal.status === "Approved" && !isExpired(deal.expiry_date);
  };

  // Helper function to check if a deal is expired (including flash sales)
  const isDealExpired = (deal) => {
    // Check flash sale deals first
    if (deal?.validity_type === "flash_sale") {
      if (deal?.flash && Array.isArray(deal.flash) && deal.flash.length > 0) {
        const isFlashExpired = deal.flash.some(flash => moment(flash.flash_end_time).isBefore(moment()));
        return deal.status === "Approved" && isFlashExpired;
      }
      return false;
    }
    // Regular deals
    return deal.status === "Approved" && isExpired(deal.expiry_date);
  };


  const getDealImageUrl = (deal) =>
    deal?.image_url || deal?.imageUrl || deal?.image || (Array.isArray(deal?.images) && deal.images[0]) || null;

  const headers = [
    { key: "id", label: "Deal ID", width: "w-[100px]" },
    { key: "title", label: "Deal Title", width: "w-[200px]" },
    { key: "company", label: "Company Name", width: "w-[150px]" },
    { key: "offer_type", label: "Offer Type", width: "w-[120px]" },
    { key: "pricing", label: "Pricing", width: "w-[150px]" },
    { key: "dateCreated", label: "Date Created", width: "w-[120px]" },
    { key: "expiryDate", label: "Expiry Date", width: "w-[120px]" },
    { key: "status", label: "Status", width: "w-[130px]" },
  ];

  const sortedData = React.useMemo(() => {
    if (isServerSide) return data;
    let sortableData = [...data];
    if (statusFilter !== "all") {
      sortableData = sortableData.filter((deal) => {
        if (statusFilter === "Pending") return deal.status === "Pending";
        if (statusFilter === "active") return isActiveDeal(deal);
        if (statusFilter === "expired") return isDealExpired(deal);
        if (statusFilter === "Rejected") return deal.status === "Rejected";
        return true;
      });
    }
    sortableData.sort((a, b) => {
      const aIsPending = a.status === "Pending";
      const bIsPending = b.status === "Pending";
      if (aIsPending && !bIsPending) return -1;
      if (!aIsPending && bIsPending) return 1;
      if (sortConfig?.key) {
        let aValue = a[sortConfig.key];
        let bValue = b[sortConfig.key];
        if (sortConfig.key === "dateCreated") {
          aValue = new Date(a.created_at);
          bValue = new Date(b.created_at);
        } else if (sortConfig.key === "expiryDate") {
          aValue = new Date(a.expiry_date);
          bValue = new Date(b.expiry_date);
        }
        if (aValue < bValue) return sortConfig.direction === "ascending" ? -1 : 1;
        if (aValue > bValue) return sortConfig.direction === "ascending" ? 1 : -1;
        return 0;
      }
      return new Date(b.created_at) - new Date(a.created_at);
    });
    return sortableData;
  }, [data, sortConfig, statusFilter, isServerSide]);

  const filteredData = React.useMemo(() => {
    if (isServerSide) return data;
    return sortedData.filter((item) =>
      Object.values(item).some(
        (val) =>
          val != null &&
          val.toString().toLowerCase().includes((searchTerm || "").toLowerCase())
      )
    );
  }, [isServerSide, data, sortedData, searchTerm]);

  const totalPages = isServerSide
    ? serverPagination?.totalPages ?? 1
    : Math.ceil(filteredData.length / clientItemsPerPage);
  const currentPage = isServerSide
    ? serverPagination?.currentPage ?? 1
    : clientPage;
  const itemsPerPage = isServerSide
    ? serverPagination?.itemsPerPage ?? 15
    : clientItemsPerPage;
  const totalItems = isServerSide
    ? serverPagination?.totalItems ?? 0
    : filteredData.length;
  const indexOfFirstItem = (currentPage - 1) * itemsPerPage;
  const indexOfLastItem = Math.min(
    currentPage * itemsPerPage,
    isServerSide ? totalItems : filteredData.length
  );
  const currentItems = isServerSide
    ? data
    : filteredData.slice(indexOfFirstItem, indexOfLastItem);

  const paginate = (pageNumber) => {
    if (isServerSide) onPageChange(pageNumber);
    else setClientPage(pageNumber);
  };

  const isEmpty = currentItems.length === 0 && !isLoading;

  return (
    <div className="flex relative flex-col">
      {/* Loading overlay when server-side and fetching */}
      {isServerSide && isLoading && (
        <div className="absolute inset-0 z-30 flex items-center justify-center min-h-[280px] rounded-lg border border-gray-200 bg-white/85 backdrop-blur-[2px]">
          <div className="flex flex-col gap-3 items-center">
            <Loader2 className="w-10 h-10 text-[#FF6C6C] animate-spin" strokeWidth={2} />
            <span className="text-sm font-medium text-gray-600">Loading deals...</span>
          </div>
        </div>
      )}

      {isEmpty && (
        <div className="flex flex-col items-center justify-center min-h-[320px] px-6 py-12 rounded-lg border border-gray-200 bg-gradient-to-b from-gray-50/80 to-white">
          <div className="flex items-center justify-center w-20 h-20 rounded-full bg-[#FF6C6C]/10 border border-[#FF6C6C]/20 mb-5">
            <Inbox className="w-10 h-10 text-[#FF6C6C]" strokeWidth={1.5} />
          </div>
          <h3 className="mb-2 text-lg font-semibold text-gray-900">No deals found</h3>
          <p className="mb-1 max-w-sm text-sm text-center text-gray-500">
            {(searchTerm || statusFilter !== "all")
              ? "Try adjusting your search or filters to see more results."
              : "There are no deals to display at the moment. New deals will appear here once submitted."}
          </p>
          {typeof onClearFilters === "function" && (searchTerm || statusFilter !== "all") && (
            <button
              type="button"
              onClick={onClearFilters}
              className="mt-4 px-4 py-2 text-sm font-medium text-[#FF6C6C] hover:bg-[#FF6C6C]/10 rounded-lg border border-[#FF6C6C]/30 transition-colors"
            >
              Clear filters
            </button>
          )}
        </div>
      )}

      {!isEmpty && (
      <>
      <div className="overflow-x-auto">
        <div className="inline-block min-w-full align-middle">
          <div className="overflow-hidden relative rounded-lg border">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="sticky top-0 z-20 bg-gray-50">
                <tr>
                  <th className="w-[72px] px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Image
                  </th>
                  {headers.map((header) => (
                    <th
                      key={header.key}
                      className={`${header.width} px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 truncate`}
                      onClick={() => onSort(header.key)}
                    >
                      <div className="flex items-center space-x-1">
                        <span className="truncate">{header.label}</span>
                        <div className="flex flex-col">
                          <FiChevronUp
                            className={`w-4 h-4 ${
                              sortConfig.key === header.key &&
                              sortConfig.direction === "ascending"
                                ? "text-[#FF6C6C]"
                                : "text-gray-400"
                            }`}
                          />
                          <FiChevronDown
                            className={`w-4 h-4 -mt-2 ${
                              sortConfig.key === header.key &&
                              sortConfig.direction === "descending"
                                ? "text-[#FF6C6C]"
                                : "text-gray-400"
                            }`}
                          />
                        </div>
                      </div>
                    </th>
                  ))}
                  <th className="w-[160px] px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {currentItems.map((deal, index) => (
                  <motion.tr
                    key={deal.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.05 }}
                    className="hover:bg-gray-50"
                  >
                    <td className="px-3 py-4 whitespace-nowrap">
                      <DealImageThumbnail deal={deal} getDealImageUrl={getDealImageUrl} />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-gray-100 border border-gray-200/80 font-mono text-xs font-semibold text-gray-700 tracking-tight shadow-sm">
                        <span className="font-sans text-gray-400">#</span>
                        {deal.id}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900 whitespace-nowrap">
                      {truncateSentence(deal.title, 3)}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500 whitespace-nowrap">
                      {deal.company_name}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        {(() => {
                          // Determine offer type based on data (same logic as vendor side)
                          if (deal.bundle && deal.bundle.length > 0 && 
                              deal.bundle.some(bundle => bundle.original_sum && bundle.bundle_price)) {
                            return (
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-700">
                                Bundle
                              </span>
                            );
                          } else if (deal.bogo && deal.bogo.length > 0 && 
                                     deal.bogo.some(bogo => bogo.bogo_item_price || (bogo.bogo_quantity && bogo.bogo_free_quantity))) {
                            return (
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-700">
                                BOGO
                              </span>
                            );
                          } else if (deal.price && deal.price !== 0) {
                            return (
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">
                                Discount
                              </span>
                            );
                          } else {
                            return (
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-700">
                                {deal.offer_type || 'Standard'}
                              </span>
                            );
                          }
                        })()}
                      </div>
                    </td>
                    <td className="px-6 py-2 align-middle whitespace-nowrap">
                      <div className="text-xs text-gray-900 flex flex-wrap items-baseline gap-x-1.5 gap-y-0">
                        {(() => {
                          if (deal.bundle && deal.bundle.length > 0 &&
                              deal.bundle.some(bundle => bundle.original_sum && bundle.bundle_price)) {
                            const bundleItem = deal.bundle[0];
                            return (
                              <>
                                <span className="font-medium text-purple-600">{formatCurrency(bundleItem.bundle_price)}</span>
                                <span className="text-gray-400">·</span>
                                <span className="text-gray-500">Save {formatCurrency(bundleItem.bundle_savings)}</span>
                              </>
                            );
                          }
                          if (deal.bogo && deal.bogo.length > 0 &&
                              deal.bogo.some(bogo => bogo.bogo_item_price || (bogo.bogo_quantity && bogo.bogo_free_quantity))) {
                            const bogoItem = deal.bogo[0];
                            return (
                              <>
                                <span className="font-medium text-blue-600">{formatCurrency(bogoItem.bogo_item_price)}</span>
                                <span className="text-gray-400">·</span>
                                <span className="text-gray-500">Buy {bogoItem.bogo_quantity} Get {bogoItem.bogo_free_quantity}</span>
                              </>
                            );
                          }
                          if (deal.price && deal.price !== 0) {
                            return (
                              <>
                                {deal.discounted_price && deal.discounted_price !== 0 ? (
                                  <>
                                    <span className="text-gray-400 line-through">{formatCurrency(deal.price)}</span>
                                    <span className="font-medium text-green-600">{formatCurrency(deal.discounted_price)}</span>
                                    {deal.discount_percentage > 0 && (
                                      <span className="text-pink-600">({deal.discount_percentage}% off)</span>
                                    )}
                                  </>
                                ) : (
                                  <span className="font-medium text-gray-600">{formatCurrency(deal.price)}</span>
                                )}
                              </>
                            );
                          }
                          return <span className="text-gray-400">—</span>;
                        })()}
                      </div>
                    </td>


                    <td className="px-6 py-4 text-sm text-gray-500 whitespace-nowrap">
                      {formatDate(deal.created_at)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        {(() => {
                          // Check if it's a flash sale deal
                          if (deal?.validity_type === "flash_sale") {
                            return (
                              <span className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium bg-gradient-to-r from-red-50 to-orange-50 text-red-700 border border-red-200">
                                <svg className="w-3 h-3 mr-1.5 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                                </svg>
                                Flash Sale Deal
                              </span>
                            );
                          }
                          
                          // Regular expiry date logic
                          if (deal.expiry_date) {
                            return (
                              <span
                                className={`inline-flex items-center px-2.5 py-1 rounded-md transition-colors duration-200
                                  ${
                                    isExpired(deal.expiry_date)
                                      ? "bg-red-100 text-red-800"
                                      : "text-gray-800 bg-gray-100 hover:bg-gray-200"
                                  }`}
                              >
                                {moment(deal.expiry_date).format("YYYY-MMM-DD")}
                              </span>
                            );
                          }
                          
                          return (
                            <span className="text-gray-400">No expiry date</span>
                          );
                        })()}
                      </div>
                    </td>

                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full
                          ${(() => {
                            // Check flash sale first - this takes priority over regular status
                            if (deal?.validity_type === "flash_sale") {
                              // If flash data exists and is valid
                              if (deal?.flash && Array.isArray(deal.flash) && deal.flash.length > 0) {
                                const isFlashExpired = deal.flash.some(flash => moment(flash.flash_end_time).isBefore(moment()));
                                const isFlashActive = deal.flash.some(flash => 
                                  moment().isBetween(moment(flash.flash_start_time), moment(flash.flash_end_time))
                                );
                                
                                // Flash Sale Active - only if approved and not expired
                                if (isFlashActive && deal.status === "Approved") {
                                  return "bg-green-100 text-green-800 animate-pulse";
                                }
                                
                                // For non-approved deals (Pending/Rejected), return their appropriate colors
                                if (deal.status === "Pending") return "bg-yellow-100 text-yellow-800";
                                if (deal.status === "Rejected") return "bg-red-100 text-red-800";
                                
                                // Flash Sale Expired (only for approved deals)
                                if (isFlashExpired) return "bg-red-100 text-red-800";
                                
                                // Flash Sale Scheduled (approved but not yet active)
                                return "bg-orange-100 text-orange-800";
                              }
                              // Flash sale validity type but no flash data
                              if (deal.status === "Approved") return "bg-orange-100 text-orange-800";
                              if (deal.status === "Rejected") return "bg-red-100 text-red-800";
                              return "bg-yellow-100 text-yellow-800";
                            }
                            
                            // Regular status checks (only for non-flash-sale deals)
                            if (isExpired(deal.expiry_date)) return "bg-red-100 text-red-800";
                            if (deal.status === "Approved") return "bg-green-100 text-green-800";
                            if (deal.status === "Rejected") return "bg-red-100 text-red-800";
                            return "bg-yellow-100 text-yellow-800";
                          })()}`}
                      >
                        {(() => {
                          // Flash sale status - takes priority over regular status
                          if (deal?.validity_type === "flash_sale") {
                            // If flash data exists and is valid
                            if (deal?.flash && Array.isArray(deal.flash) && deal.flash.length > 0) {
                              const isFlashExpired = deal.flash.some(flash => moment(flash.flash_end_time).isBefore(moment()));
                              const isFlashActive = deal.flash.some(flash => 
                                moment().isBetween(moment(flash.flash_start_time), moment(flash.flash_end_time))
                              );
                              
                              // Flash Sale Active - only if approved
                              if (isFlashActive && deal.status === "Approved") {
                                return "Flash Sale Active";
                              }
                              
                              // For non-approved deals (Pending/Rejected), return their actual status
                              if (deal.status !== "Approved") {
                                return deal.status;
                              }
                              
                              // Flash Sale Expired (only for approved deals)
                              if (isFlashExpired) return "Flash Sale Expired";
                              
                              // Flash Sale Scheduled (approved but not active yet)
                              return "Flash Sale Scheduled";
                            }
                            // Flash sale validity type but no flash data
                            if (deal.status === "Approved") return "Flash Sale Scheduled";
                            return "Pending";
                          }
                          
                          // Regular status (only for non-flash-sale deals)
                          if (isExpired(deal.expiry_date)) return "Expired";
                          return deal.status;
                        })()}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500 whitespace-nowrap">
                      <ActionMenu
                        row={deal}
                        onSuccess={refreshData}
                        isFirstRow={index === 0}
                        totalRows={currentItems.length}
                      />
                    </td>
                  </motion.tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <div className="flex justify-between items-center px-4 py-5 bg-white border-t border-gray-200 sm:px-6">
        <div className="flex flex-1 items-center">
          <p className="text-sm text-gray-700">
            Showing <span className="font-medium">{indexOfFirstItem + 1}</span>{" "}
            to{" "}
            <span className="font-medium">
              {isServerSide
                ? indexOfFirstItem + currentItems.length
                : Math.min(indexOfLastItem, filteredData.length)}
            </span>{" "}
            of <span className="font-medium">{isServerSide ? totalItems : filteredData.length}</span>{" "}
            results
          </p>
        </div>

        <div className="flex gap-6 items-center">
          <div className="flex gap-2 items-center">
            <button
              onClick={() => paginate(currentPage - 1)}
              disabled={currentPage === 1}
              className={`p-2 rounded-lg inline-flex items-center justify-center transition-colors
                ${
                  currentPage === 1
                    ? "text-gray-300 cursor-not-allowed"
                    : "text-gray-500 hover:text-white hover:bg-[#FF6C6C] bg-gray-100"
                }`}
            >
              <FiChevronLeft className="w-5 h-5" />
            </button>

            <div className="flex gap-1 items-center">
              {[...Array(Math.max(1, totalPages))].map((_, index) => {
                const pageNumber = index + 1;
                if (
                  pageNumber === 1 ||
                  pageNumber === totalPages ||
                  (pageNumber >= currentPage - 1 && pageNumber <= currentPage + 1)
                ) {
                  return (
                    <button
                      key={index}
                      onClick={() => paginate(pageNumber)}
                      className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors
                        ${
                          currentPage === pageNumber
                            ? "bg-red-100 text-[#FF6C6C] border border-red-200"
                            : "text-gray-500 hover:bg-gray-100"
                        }`}
                    >
                      {pageNumber}
                    </button>
                  );
                } else if (
                  pageNumber === currentPage - 2 ||
                  pageNumber === currentPage + 2
                ) {
                  return (
                    <span key={index} className="px-2 text-gray-400">
                      ...
                    </span>
                  );
                }
                return null;
              })}
            </div>

            <button
              onClick={() => paginate(currentPage + 1)}
              disabled={currentPage === totalPages}
              className={`p-2 rounded-lg inline-flex items-center justify-center transition-colors
                ${
                  currentPage === totalPages
                    ? "text-gray-300 cursor-not-allowed"
                    : "text-gray-500 hover:text-white hover:bg-[#FF6C6C] bg-gray-100"
                }`}
            >
              <FiChevronRight className="w-5 h-5" />
            </button>
          </div>

          <select
            className="border-red-200 border rounded-lg text-sm text-white py-1.5 px-3 pr-8 bg-[#FF6C6C] hover:border-gray-300 focus:outline-none focus:ring-2 focus:ring-red-200 focus:border-red-200"
            value={itemsPerPage}
            onChange={(e) => {
              const val = Number(e.target.value);
              if (isServerSide && onItemsPerPageChange) {
                onItemsPerPageChange(val);
              } else {
                setClientItemsPerPage(val);
                setClientPage(1);
              }
            }}
          >
            <option value={5}>5 / page</option>
            <option value={10}>10 / page</option>
            <option value={15}>15 / page</option>
            <option value={20}>20 / page</option>
            <option value={50}>50 / page</option>
          </select>
        </div>
      </div>
      </>
      )}
    </div>
  );
};

export default TableComponent;